import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import apiClient from "../../utils/api";
import { toast } from 'react-toastify';

function IncidentsEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        car_plate: '',
        driver_id: '',
        incident_description: '',
        date_time: '',
        images: [],
        supervisor_approval: false,
        existing_images: []
    });
    const [drivers, setDrivers] = useState([]);

    const fetchDrivers = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/drivers', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDrivers(response.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to fetch drivers');
        }
    };

    const fetchIncident = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/incidents/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const incident = response.data.data;
            
            // Format the date_time to work with datetime-local input
            const formattedDateTime = incident.date_time ? 
                new Date(incident.date_time).toISOString().slice(0, 16) : 
                '';

            setFormData({
                car_plate: incident.car_plate,
                driver_id: incident.driver_id,
                incident_description: incident.incident_description,
                date_time: formattedDateTime, // Use the formatted date
                supervisor_approval: incident.supervisor_approval,
                existing_images: incident.images || [],
                images: []
            });
        } catch (error) {
            console.error('Error fetching incident:', error);
            toast.error('Failed to fetch incident');
        }
    };

    useEffect(() => {
        fetchDrivers();
        fetchIncident();
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        if (type === 'file') {
            setFormData({
                ...formData,
                [name]: files
            });
        } else {
            setFormData({
                ...formData,
                [name]: type === 'checkbox' ? checked : value
            });
        }
    };

    const handleImageDelete = async (imageIndex) => {
        try {
            await apiClient.delete(`accounts/incidents/${id}/images/${imageIndex}`);
            setFormData(prev => ({
                ...prev,
                existing_images: prev.existing_images.filter((_, idx) => idx !== imageIndex)
            }));
            toast.success('Image deleted successfully');
        } catch (error) {
            console.error('Error deleting image:', error);
            toast.error('Failed to delete image');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('auth_token');
            const formPayload = new FormData();
            Object.keys(formData).forEach(key => {
                if (key === 'images') {
                    for (let i = 0; i < formData.images.length; i++) {
                        formPayload.append('images[]', formData.images[i]);
                    }
                } else if (key !== 'existing_images') {
                    formPayload.append(key, formData[key]);
                }
            });
            formPayload.append('_method', 'PUT');

            await apiClient.post(`accounts/incidents/${id}`, formPayload, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                }
            });
            navigate('/accounts/incidents-list');
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="container-xxl">
            <PageHeader1 pagetitle="Edit Incident" />
            <div className="row g-3">
                <div className="col-xl-12">
                    <div className="card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Car Plate Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="car_plate"
                                            value={formData.car_plate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Driver</label>
                                        <select
                                            className="form-control"
                                            name="driver_id"
                                            value={formData.driver_id}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Driver</option>
                                            {drivers.map(driver => (
                                                <option key={driver.id} value={driver.id}>
                                                    {driver.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-12">
                                        <label className="form-label">Incident Description</label>
                                        <textarea
                                            className="form-control"
                                            name="incident_description"
                                            value={formData.incident_description}
                                            onChange={handleInputChange}
                                            rows="4"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Date & Time</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            name="date_time"
                                            value={formData.date_time}
                                            readOnly
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Upload New Images</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            name="images"
                                            onChange={handleInputChange}
                                            multiple
                                            accept="image/*"
                                        />
                                    </div>
                                    {formData.existing_images.length > 0 && (
                                        <div className="col-12">
                                            <label className="form-label">Existing Images</label>
                                            <div className="row g-2">
                                                {formData.existing_images.map((image, index) => (
                                                    <div key={index} className="col-auto">
                                                        <div className="position-relative">
                                                            <a  href={`${process.env.REACT_APP_STORAGE_URL}${image.url}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img 
                                                                    src={`${process.env.REACT_APP_STORAGE_URL}${image.url}`} 
                                                                    alt={`Incident ${index + 1}`}
                                                                    className="img-thumbnail"
                                                                    style={{
                                                                        height: "100px",
                                                                        cursor: "pointer"
                                                                    }}
                                                                />
                                                            </a>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                                                onClick={() => handleImageDelete(index)}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-12">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="supervisor_approval"
                                                checked={formData.supervisor_approval}
                                                onChange={handleInputChange}
                                            />
                                            <label className="form-check-label">Supervisor Approval</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <button type="submit" className="btn btn-primary me-2">Update</button>
                                    <button type="button" className="btn btn-secondary" onClick={handlePrint}>
                                        Print Receipt
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default IncidentsEdit;